import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

// Create Loading Context
const LoadingContext = createContext();

// Loading Provider Component
export const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('googleToken'));

  // Update token state when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setToken(localStorage.getItem('googleToken'));
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Create Axios instance with interceptors
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      //baseURL: 'https://localhost:7267', // Replace with your API URL
      baseURL: 'https://app.lakhandahana.com/api', // Replace with your API URL
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    let activeRequests = 0;

    instance.interceptors.request.use(
      (config) => {
        activeRequests++;
        setLoading(true); // Set loading state to true
        return config;
      },
      (error) => {
        activeRequests--;
        if (activeRequests === 0) setLoading(false); // Reset loading state
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      (response) => {
        activeRequests--;
        if (activeRequests === 0) setLoading(false); // Reset loading state
        return response;
      },
      (error) => {
        activeRequests--;
        if (activeRequests === 0) setLoading(false); // Reset loading state

        // Session expired/invalid - reload so the app redirects to login
        if (error?.response?.status === 401) {
          localStorage.removeItem('googleToken');
          window.location.reload();
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [token]); // Ensure instance is created only once

  return (
    <LoadingContext.Provider value={{ loading, axiosInstance }}>
      {children}
    </LoadingContext.Provider>
  );
};

LoadingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom Hook to Access Loading Context
export const useLoading = () => useContext(LoadingContext);
